/*
 * This code is protected by intellectual property rights.
 * Dr. Ing. h.c. F. Porsche AG owns exclusive rights of use.
 * © 2017-2026, Dr. Ing. h.c. F. Porsche AG.
 */

import { Injectable, inject } from '@angular/core'
import { TranslateService } from '@ngx-translate/core'
import { mergeMap } from 'rxjs/operators'
import { EMPTY, from, lastValueFrom, Observable, of, Subject } from 'rxjs'
import { ConfigService } from '../configuration/config.service'
import { CustomLocaleLoaderService } from '../custom-locale-loader.service'
import { LocaleInformation } from '../configuration/config.interface'
import { WINDOW, WindowWrapper } from '@myprofile-ui/pidx-utils'

export abstract class LocaleHandlingConfig {
  localeHeaderWhitelistUrl?: string | RegExp | (string | RegExp)[]
  localeHeaderNameCountry?: string
  localeHeaderNameLocale?: string
}

@Injectable({
  providedIn: 'root',
})
export class LocaleHandlingService {
  private window = inject<WindowWrapper>(WINDOW)
  private localeHandlingConfig = inject(LocaleHandlingConfig, { optional: true })
  protected translate = inject(TranslateService)
  protected configService = inject(ConfigService)
  protected customLocaleLoaderService = inject(CustomLocaleLoaderService)

  private readonly defaultConfig: LocaleHandlingConfig = {
    localeHeaderWhitelistUrl: undefined,
    localeHeaderNameCountry: 'x-vrs-url-country',
    localeHeaderNameLocale: 'x-vrs-url-language',
  }

  private readonly config: LocaleHandlingConfig
  private initializedSubject = new Subject<void>()

  constructor() {
    this.config = {
      ...this.defaultConfig,
      ...this.localeHandlingConfig,
    }
  }

  check(localeInformation: LocaleInformation, validLocale: () => Promise<boolean>): Promise<boolean> {
    if (localeInformation && localeInformation.translationFileName && localeInformation.angularLocale) {
      return validLocale()
    }
    return Promise.resolve(false)
  }

  async setup(): Promise<boolean> {
    const result = await this.check(this.configService.getLocaleInformation(), () => this.validLocalePromise())

    this.initializedSubject.next()
    this.initializedSubject.complete()
    return result
  }

  getConfig(): LocaleHandlingConfig {
    return this.config
  }

  private async validLocalePromise(): Promise<boolean> {
    const angularLocale = this.configService.getAngularLocale()
    const fallbackTranslationFilename = this.configService.getFallbackTranslationFileName()

    this.update(angularLocale)

    await Promise.all([
      lastValueFrom(this.translate.use(this.configService.getTranslationFileName())),
      this.loadFallbackTranslationsIfAvailable(fallbackTranslationFilename),
      this.loadAngularLocaleData(angularLocale),
    ])

    return true
  }

  private async loadFallbackTranslationsIfAvailable(fallbackTranslationFilename: string): Promise<string> {
    return lastValueFrom(this.queue(fallbackTranslationFilename).pipe(mergeMap((locale) => from(this.loadFallbackTranslations(locale)))))
  }

  queue(fallback: string): Observable<string> {
    if (fallback) {
      return of(fallback)
    }

    return EMPTY
  }

  update(locale: string) {
    const htmlElement = document.querySelector('html')
    if (htmlElement) {
      htmlElement.lang = locale
    }
  }

  private async loadFallbackTranslations(locale: string): Promise<string> {
    /**
     * preload translations because setDefaultLang doesn't provide
     * an observable that tells if the translations are ready.
     * however, if they are already loaded it's synchronous
     */
    await lastValueFrom(this.translate.get(locale))
    this.translate.setFallbackLang(locale)
    return locale
  }

  private async loadAngularLocaleData(angularLocale: string): Promise<string> {
    return lastValueFrom(this.customLocaleLoaderService.load(angularLocale))
  }
}
